import { MAX_COMMAND_SELECTION, type EditorDocument, type Item, type NoteContent } from '@mystiq/contracts';
import { noteExtensions } from '@mystiq/editor/extensions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getTextBetween, getTextSerializersFromSchema } from '@tiptap/core';
import { Placeholder } from '@tiptap/extensions';
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../api/client.ts';
import { keys } from '../api/hooks.ts';
import { useApi, useSession } from '../auth/session.tsx';
import { CommandBar } from '../components/CommandBar.tsx';
import { useComments } from '../components/Comments.tsx';
import { ReadAloudControl } from '../components/ReadAloud.tsx';
import { Modal } from '../components/ui.tsx';
import { useDictation, useDictationTarget } from '../dictation/Dictation.tsx';
import { shortcutLabel } from '../dictation/shortcut.ts';
import { replacementContent } from './replacement.ts';
import {
  Bold,
  BulletList,
  Code,
  CodeBlock,
  Comment,
  Divider,
  Heading1,
  Heading2,
  ICON,
  Italic,
  Link,
  Mic,
  NumberedList,
  Quote,
  Redo,
  Strike,
  Undo,
} from '../icons.ts';
import { AutosaveController, SaveFailure, type AutosaveState } from './autosave.ts';
import { draftKey, localDraftStore, type StoredDraft } from './drafts.ts';

const extensions = [...noteExtensions(), Placeholder.configure({ placeholder: 'Start writing…' })];

/** Loads the saved note and mounts the editor once; later loads are treated as remote updates. */
export function NoteBody({ ws, item }: { ws: string; item: Item }) {
  const api = useApi();
  const note = useQuery({ queryKey: keys.note(ws, item.id), queryFn: () => api.readNote(ws, item.id) });
  if (note.isLoading || !note.data) {
    return <p className="meta">{note.isError ? 'Could not load this note.' : 'Loading…'}</p>;
  }
  return <NoteEditor ws={ws} item={item} saved={note.data} />;
}

function NoteEditor({ ws, item, saved }: { ws: string; item: Item; saved: NoteContent }) {
  const api = useApi();
  const session = useSession();
  const client = useQueryClient();
  const editable = item.permittedActions.includes('edit');
  const comments = useComments();
  const [commentProblem, setCommentProblem] = useState<string | null>(null);
  const drafts = useMemo(() => localDraftStore<EditorDocument>(draftKey(session.userKey, item.id)), [session.userKey, item.id]);
  const [state, setState] = useState<AutosaveState>({ status: 'saved', revisionId: saved.revisionId });
  const [recovery, setRecovery] = useState<StoredDraft<EditorDocument> | null>(null);

  const controllerRef = useRef<AutosaveController<EditorDocument> | null>(null);
  // The selection a command was asked about, for applying a rewrite to exactly that text.
  const selectionRef = useRef<{ from: number; to: number; text: string } | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new AutosaveController<EditorDocument>({
      baseRevisionId: saved.revisionId,
      draft: drafts,
      onChange: setState,
      save: async (base, document, idempotencyKey) => {
        try {
          const outcome = await api.saveNote(ws, item.id, base, document, idempotencyKey);
          if (outcome.status === 'conflict') return { status: 'conflict', currentRevisionId: outcome.currentRevisionId };
          // Keep the cache on the saved revision so reopening this note starts from it, not an older copy.
          client.setQueryData(keys.note(ws, item.id), (old: NoteContent | undefined) =>
            old ? { ...old, revisionId: outcome.revisionId, document } : old,
          );
          return { status: 'saved', revisionId: outcome.revisionId };
        } catch (error) {
          if (error instanceof ApiError) {
            const retryable = error.status === 0 || error.status >= 500 || error.status === 429;
            throw new SaveFailure(retryable ? 'Could not reach Mystiq.' : error.message, retryable);
          }
          throw new SaveFailure('Could not reach Mystiq.', true);
        }
      },
    });
  }
  const controller = controllerRef.current;

  const editor = useEditor({
    extensions,
    content: saved.document,
    editable,
    immediatelyRender: true,
    // Screen readers announce the note's text as a named, multi-line text field.
    editorProps: { attributes: { role: 'textbox', 'aria-multiline': 'true', 'aria-label': 'Note text', ...(editable ? {} : { 'aria-readonly': 'true' }) } },
    onUpdate: ({ editor: instance }) => controller.edit(instance.getJSON() as EditorDocument),
  });
  useDictationTarget(editable ? editor : null);

  /** The selected text as character offsets in the note's plain text (the same counting as the server's). */
  const selectedRange = () => {
    const { from, to, empty } = editor.state.selection;
    if (empty) return null;
    const options = { blockSeparator: '\n\n', textSerializers: getTextSerializersFromSchema(editor.schema) };
    const start = getTextBetween(editor.state.doc, { from: 0, to: from }, options).length;
    return { start, end: start + getTextBetween(editor.state.doc, { from, to }, options).length };
  };

  // Recover an unsent draft from this browser (same person only).
  useEffect(() => {
    const draft = drafts.read();
    if (!draft || !editable) return;
    if (draft.base === saved.revisionId) {
      editor.commands.setContent(draft.doc, { emitUpdate: false });
      controller.edit(draft.doc);
    } else {
      setRecovery(draft);
    }
    // Only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Newer saved content from elsewhere (an agent or another tab): load it only if nothing local is unsaved.
  useEffect(() => {
    if (saved.revisionId === controller.baseRevisionId) return;
    if (controller.acceptRemote(saved.revisionId)) editor.commands.setContent(saved.document, { emitUpdate: false });
  }, [saved.revisionId, saved.document, controller, editor]);

  // Save before leaving: on navigation (unmount) and when the tab closes.
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      drafts.flush();
      if (controller.hasUnsavedChanges) {
        void controller.flush();
        event.preventDefault();
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      drafts.flush();
      void controller.flush();
    };
  }, [controller, drafts]);

  async function adoptSavedVersion(revisionHint?: string) {
    const latest = await api.readNote(ws, item.id);
    editor.commands.setContent(latest.document, { emitUpdate: false });
    controller.discardMine(revisionHint && revisionHint === latest.revisionId ? revisionHint : latest.revisionId);
    setRecovery(null);
    client.setQueryData(keys.note(ws, item.id), latest);
  }

  return (
    <>
      <SaveStatus state={state} editable={editable} onRetry={() => controller.retry()} />
      <ReadAloudControl ws={ws} itemId={item.id} range={selectedRange} beforeStart={() => controller.flush()} />
      {item.permittedActions.includes('comment') && comments && (
        <div className="row-actions">
          <button
            className="button button-inline"
            onClick={async () => {
              const range = selectedRange();
              if (!range) return setCommentProblem('Select some words first.');
              const { from, to } = editor.state.selection;
              const quote = editor.state.doc.textBetween(from, to, ' ');
              // A comment points at saved text, so anything typed is saved first.
              await controller.flush();
              if (controller.hasUnsavedChanges) return setCommentProblem('Save the note first: some changes are not saved yet.');
              setCommentProblem(null);
              comments.propose({ anchor: { kind: 'text', ...range }, revisionId: controller.baseRevisionId, label: quote });
            }}
          >
            <Comment size={ICON.inline} stroke={ICON.stroke} />
            Comment on selection
          </button>
          {commentProblem && <span className="meta">{commentProblem}</span>}
        </div>
      )}
      {state.status === 'conflict' && (
        <div className="conflict" role="alert">
          <p>
            <span className="state">This note changed elsewhere</span> while you were editing. Your text is kept here and in this browser.
          </p>
          <div className="row-actions">
            <button className="button" onClick={() => controller.keepMine(state.currentRevisionId)}>
              Keep my version
            </button>
            <button className="button" onClick={() => void adoptSavedVersion(state.currentRevisionId)}>
              Use the saved version
            </button>
          </div>
        </div>
      )}
      {recovery && (
        <div className="conflict" role="alert">
          <p>
            <span className="state">Unsaved changes found</span> from {new Date(recovery.at).toLocaleString()}, made before this note changed elsewhere.
          </p>
          <div className="row-actions">
            <button
              className="button"
              onClick={() => {
                editor.commands.setContent(recovery.doc, { emitUpdate: false });
                controller.edit(recovery.doc);
                controller.keepMine(saved.revisionId);
                setRecovery(null);
              }}
            >
              Keep my changes
            </button>
            <button className="button" onClick={() => void adoptSavedVersion()}>
              Use the saved version
            </button>
          </div>
        </div>
      )}
      {editable && <EditorToolbar editor={editor} />}
      <div className="editor-surface">
        <EditorContent editor={editor} aria-label="Note text" />
      </div>
      {editable && (
        <CommandBar
          ws={ws}
          placeholder="Ask Mystiq about this note, or select text and ask to rewrite it"
          context={() => {
            const { from, to, empty } = editor.state.selection;
            const text = empty ? '' : editor.state.doc.textBetween(from, to, '\n');
            if (text.length > MAX_COMMAND_SELECTION) throw new Error(`Select at most ${MAX_COMMAND_SELECTION.toLocaleString()} characters to ask about.`);
            selectionRef.current = text ? { from, to, text } : null;
            return { itemIds: [item.id], ...(text ? { selection: { itemId: item.id, text } } : {}) };
          }}
          onReplacement={(replacement) => {
            const selection = selectionRef.current;
            if (!selection) return null;
            // A stale selection is not rewritten: the text must still be exactly what was selected.
            const { doc } = editor.state;
            const current = selection.to <= doc.content.size ? doc.textBetween(selection.from, selection.to, '\n') : null;
            if (current !== selection.text) return 'The selected text changed meanwhile, so it was not replaced. Select it again.';
            const range = { from: selection.from, to: selection.to };
            const content = replacementContent(replacement);
            const chain = editor.chain().focus();
            (content.length ? chain.insertContentAt(range, content) : chain.deleteRange(range)).run();
            return null;
          }}
        />
      )}
    </>
  );
}

function SaveStatus({ state, editable, onRetry }: { state: AutosaveState; editable: boolean; onRetry(): void }) {
  if (!editable) return <p className="save-status">Read-only: your role can view this note.</p>;
  let content;
  switch (state.status) {
    case 'saved':
      content = 'Saved';
      break;
    case 'pending':
      content = 'Unsaved changes';
      break;
    case 'saving':
      content = 'Saving…';
      break;
    case 'conflict':
      content = <span className="state">Not saved: changed elsewhere</span>;
      break;
    case 'error':
      content = (
        <>
          <span className="state">Save failed.</span> {state.message} {state.retrying ? 'Retrying automatically.' : ''}
          <button className="link-button" onClick={onRetry}>
            Retry now
          </button>
        </>
      );
      break;
  }
  return (
    <p className="save-status" role="status" aria-live="polite" data-save-state={state.status}>
      {content}
    </p>
  );
}

function EditorToolbar({ editor }: { editor: Editor }) {
  const [linking, setLinking] = useState(false);
  const dictation = useDictation();
  const active = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      strike: e.isActive('strike'),
      code: e.isActive('code'),
      h1: e.isActive('heading', { level: 1 }),
      h2: e.isActive('heading', { level: 2 }),
      bullet: e.isActive('bulletList'),
      ordered: e.isActive('orderedList'),
      quote: e.isActive('blockquote'),
      codeBlock: e.isActive('codeBlock'),
      link: e.isActive('link'),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  });
  const chain = () => editor.chain().focus();
  const tool = (label: string, Icon: typeof Bold, pressed: boolean | undefined, run: () => void, disabled = false) => (
    <button
      type="button"
      className="icon-button"
      aria-label={label}
      title={label}
      aria-pressed={pressed === undefined ? undefined : pressed}
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={run}
    >
      <Icon size={ICON.nav} stroke={ICON.stroke} />
    </button>
  );
  return (
    <div className="editor-toolbar" role="toolbar" aria-label="Formatting">
      {tool('Bold', Bold, active.bold, () => chain().toggleBold().run())}
      {tool('Italic', Italic, active.italic, () => chain().toggleItalic().run())}
      {tool('Strikethrough', Strike, active.strike, () => chain().toggleStrike().run())}
      {tool('Inline code', Code, active.code, () => chain().toggleCode().run())}
      {tool('Link', Link, active.link, () => (active.link ? chain().extendMarkRange('link').unsetLink().run() : setLinking(true)))}
      <span className="toolbar-gap" />
      {tool('Heading', Heading1, active.h1, () => chain().toggleHeading({ level: 1 }).run())}
      {tool('Subheading', Heading2, active.h2, () => chain().toggleHeading({ level: 2 }).run())}
      {tool('Bulleted list', BulletList, active.bullet, () => chain().toggleBulletList().run())}
      {tool('Numbered list', NumberedList, active.ordered, () => chain().toggleOrderedList().run())}
      {tool('Quote', Quote, active.quote, () => chain().toggleBlockquote().run())}
      {tool('Code block', CodeBlock, active.codeBlock, () => chain().toggleCodeBlock().run())}
      {tool('Divider', Divider, undefined, () => chain().setHorizontalRule().run())}
      <span className="toolbar-gap" />
      {tool('Undo', Undo, undefined, () => chain().undo().run(), !active.canUndo)}
      {tool('Redo', Redo, undefined, () => chain().redo().run(), !active.canRedo)}
      {dictation && (
        <>
          <span className="toolbar-gap" />
          {tool(`Dictate (${shortcutLabel(dictation.shortcut)})`, Mic, undefined, () => {
            // Keep the place in the note: the editor has the focus when dictation starts.
            if (!editor.isFocused) editor.commands.focus();
            dictation.toggle();
          })}
        </>
      )}
      {linking && <LinkDialog onClose={() => setLinking(false)} onSave={(href) => chain().extendMarkRange('link').setLink({ href }).run()} />}
    </div>
  );
}

function LinkDialog({ onSave, onClose }: { onSave(href: string): void; onClose(): void }) {
  const [href, setHref] = useState('https://');
  const valid = /^(https?:\/\/|mailto:)\S+$/i.test(href);
  return (
    <Modal title="Add link" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(event) => {
          event.preventDefault();
          if (!valid) return;
          onSave(href);
          onClose();
        }}
      >
        <label className="field">
          <span className="field-label">Link address</span>
          <input className="input" value={href} onChange={(event) => setHref(event.target.value)} />
          <span className="meta">Web (https://) and email (mailto:) links only.</span>
        </label>
        <div className="modal-actions">
          <button className="button button-primary" type="submit" disabled={!valid}>
            Add link
          </button>
          <button className="button" type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Modal>
  );
}
